import { useEffect, useState } from 'react';
import {
  LayoutDashboard,
  Plug,
  GitBranch,
  ListChecks,
  Copy,
  Upload,
  BarChart3,
  Settings as SettingsIcon,
  Bell,
  Search,
  Cpu,
  Globe,
  Tag,
  ShieldCheck,
  Users,
  PhoneCall,
} from 'lucide-react';
import PublicApp from '@/public/PublicApp';
import OwnerConsole from '@/public/OwnerConsole';
import Overview from '@/pages/Overview';
import Connectors from '@/pages/Connectors';
import Pipeline from '@/pages/Pipeline';
import Listings from '@/pages/Listings';
import BusinessOwners from '@/pages/BusinessOwners';
import OffersEventsTracker from '@/pages/OffersEventsTracker';
import VisitorsLog from '@/pages/VisitorsLog';
import Duplicates from '@/pages/Duplicates';
import BulkImport from '@/pages/BulkImport';
import Analytics from '@/pages/Analytics';
import SettingsPage from '@/pages/Settings';
import CategoriesManager from '@/pages/CategoriesManager';
import CitiesManager from '@/pages/CitiesManager';
import { ClaimsManager } from '@/pages/ClaimsManager';
import GooglePlacesImport from '@/pages/GooglePlacesImport';
import BlogManagement from '@/pages/BlogManagement';
import MenuManagement from '@/pages/MenuManagement';
import AutomationHub from '@/pages/AutomationHub';
import ImageEnrichmentAdmin from '@/pages/ImageEnrichmentAdmin';
import PhoneEnrichmentAdmin from '@/pages/PhoneEnrichmentAdmin';
import { supabase } from '@/lib/supabase';
import { Notification } from '@/lib/types';
import { StatusDot } from '@/components/ui';

import LeadsPage from '@/pages/Leads';

type TabKey =
  | 'overview'
  | 'automation'
  | 'image_enrichment'
  | 'phone_enrichment'
  | 'connectors'
  | 'pipeline'
  | 'listings'
  | 'leads'
  | 'blogs'
  | 'menu'
  | 'categories'
  | 'cities'
  | 'claims'
  | 'business_owners'
  | 'offers_events'
  | 'users'
  | 'duplicates'
  | 'bulk'
  | 'google_import'
  | 'analytics'
  | 'settings';

interface TabItem {
  key: TabKey;
  label: string;
  icon: any;
  group: 'Engine' | 'Data' | 'Insights' | 'System';
}

const TABS: TabItem[] = [
  { key: 'overview', label: 'Overview', icon: LayoutDashboard, group: 'Engine' },
  { key: 'automation', label: 'Automation Hub', icon: Search, group: 'Engine' },
  { key: 'image_enrichment', label: 'Photo Monitor', icon: BarChart3, group: 'Engine' },
  { key: 'phone_enrichment', label: 'Phone Numbers Fix', icon: PhoneCall, group: 'Engine' },
  { key: 'pipeline', label: 'Pipeline', icon: GitBranch, group: 'Engine' },
  { key: 'connectors', label: 'Data Sources', icon: Plug, group: 'Engine' },
  { key: 'leads', label: 'Customer Leads', icon: Globe, group: 'Data' },
  { key: 'listings', label: 'Listings', icon: ListChecks, group: 'Data' },
  { key: 'business_owners', label: 'Business Owners', icon: Users, group: 'Data' },
  { key: 'blogs', label: 'Blog System', icon: ListChecks, group: 'Data' },
  { key: 'menu', label: 'Menu Management', icon: Tag, group: 'Data' },
  { key: 'categories', label: 'Categories', icon: Tag, group: 'Data' },
  { key: 'cities', label: 'Cities', icon: Globe, group: 'Data' },
  { key: 'claims', label: 'Business Claims', icon: ShieldCheck, group: 'Data' },
  { key: 'offers_events', label: 'Offers & Events', icon: LayoutDashboard, group: 'Data' },
  { key: 'users', label: 'Visitors Log', icon: LayoutDashboard, group: 'Data' },
  { key: 'duplicates', label: 'Duplicates', icon: Copy, group: 'Data' },
  { key: 'bulk', label: 'Bulk Import (CSV)', icon: Upload, group: 'Data' },
  { key: 'google_import', label: 'Google Places Import', icon: Search, group: 'Data' },
  { key: 'analytics', label: 'Analytics', icon: BarChart3, group: 'Insights' },
  { key: 'settings', label: 'Settings', icon: SettingsIcon, group: 'System' },
];

export default function App() {
  const [routePath, setRoutePath] = useState(() => window.location.pathname);

  useEffect(() => {
    const handleRoute = () => setRoutePath(window.location.pathname);
    window.addEventListener('popstate', handleRoute);
    window.addEventListener('localhub:routechange', handleRoute);
    return () => {
      window.removeEventListener('popstate', handleRoute);
      window.removeEventListener('localhub:routechange', handleRoute);
    };
  }, []);

  // Exact prefix match — '/admin' or '/admin/anything', or direct phone enrichment route.
  const isAdmin = routePath === '/admin' || routePath.startsWith('/admin/') || routePath === '/phone-enrichment' || routePath === '/phone-fix';
  const isOwner = routePath === '/owner' || routePath.startsWith('/owner/');

  if (isAdmin) {
    return <AdminConsole onExit={() => { window.location.href = '/owner'; }} />;
  }

  if (isOwner) {
    return <OwnerConsole onExit={() => { window.location.href = '/'; }} />;
  }

  return (
    <PublicApp
      onAdmin={() => {
        window.location.href = '/owner';
      }}
    />
  );
}

const getMaterialIcon = (key: TabKey): string => {
  switch (key) {
    case 'overview': return 'dashboard';
    case 'automation': return 'auto_awesome';
    case 'image_enrichment': return 'photo_library';
    case 'phone_enrichment': return 'call';
    case 'pipeline': return 'account_tree';
    case 'connectors': return 'power';
    case 'listings': return 'checklist';
    case 'business_owners': return 'badge';
    case 'offers_events': return 'local_offer';
    case 'leads': return 'person_search';
    case 'blogs': return 'article';
    case 'menu': return 'restaurant_menu';
    case 'categories': return 'category';
    case 'cities': return 'location_city';
    case 'claims': return 'verified_user';
    case 'users': return 'travel_explore';
    case 'duplicates': return 'content_copy';
    case 'bulk': return 'upload_file';
    case 'google_import': return 'travel_explore';
    case 'analytics': return 'monitoring';
    case 'settings': return 'settings';
    default: return 'dashboard';
  }
};

function AdminConsole({ onExit }: { onExit: () => void }) {
  // Parse initial tab from pathname
  const [tab, setTabState] = useState<TabKey>(() => {
    if (window.location.pathname === '/phone-enrichment' || window.location.pathname === '/phone-fix') {
      return 'phone_enrichment';
    }
    const match = window.location.pathname.match(/^\/admin\/(.+)/i);
    if (match && match[1]) {
      let key = match[1].replace(/\/$/, '') as TabKey;
      if ((key as string) === 'image-enrichment') key = 'image_enrichment' as TabKey;
      if ((key as string) === 'phone-enrichment' || (key as string) === 'phone_enrichment') key = 'phone_enrichment' as TabKey;
      if (TABS.some(t => t.key === key)) return key;
    }
    return 'overview';
  });

  const setTab = (newTab: TabKey) => {
    setTabState(newTab);
    const cleanUrl = `/admin/${newTab}`;
    try {
      window.history.pushState({}, '', cleanUrl);
    } catch {}
  };

  useEffect(() => {
    const handleRoute = () => {
      if (window.location.pathname === '/phone-enrichment' || window.location.pathname === '/phone-fix') {
        setTabState('phone_enrichment');
        return;
      }
      const match = window.location.pathname.match(/^\/admin\/(.+)/i);
      if (match && match[1]) {
        let key = match[1].replace(/\/$/, '') as TabKey;
        if ((key as string) === 'image-enrichment') key = 'image_enrichment' as TabKey;
        if ((key as string) === 'phone-enrichment' || (key as string) === 'phone_enrichment') key = 'phone_enrichment' as TabKey;
        if (TABS.some(t => t.key === key)) setTabState(key);
      }
    };
    window.addEventListener('popstate', handleRoute);
    window.addEventListener('localhub:routechange', handleRoute);
    return () => {
      window.removeEventListener('popstate', handleRoute);
      window.removeEventListener('localhub:routechange', handleRoute);
    };
  }, []);

  const [notifs, setNotifs] = useState<Notification[]>([]);
  const [openNotifs, setOpenNotifs] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);

  const fetchNotifs = () => {
    void supabase
      .from('notifications')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(20)
      .then(({ data }) => setNotifs((data as Notification[]) ?? []));
  };

  const fetchPendingCount = () => {
    void supabase
      .from('listings')
      .select('id', { count: 'exact', head: true })
      .eq('status', 'pending_approval')
      .then(({ count }) => setPendingCount(count ?? 0));
  };

  useEffect(() => {
    fetchNotifs();
    fetchPendingCount();
  }, [tab]);

  const markAsRead = async (id: string) => {
    await supabase.from('notifications').update({ read: true }).eq('id', id);
    setNotifs((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
  };

  const markAllAsRead = async () => {
    await supabase.from('notifications').update({ read: true }).eq('read', false);
    setNotifs((prev) => prev.map((n) => ({ ...n, read: true })));
  };

  const unread = notifs.filter((n) => !n.read).length;

  return (
    <div className="min-h-screen flex bg-white text-slate-800 font-body-md antialiased w-full relative">
      {/* SideNavBar (Desktop Only) */}
      <nav className="hidden md:flex flex-col h-full p-6 gap-2 fixed left-0 top-0 w-64 bg-slate-50 border-r border-slate-200 z-40 overflow-y-auto">
        <div className="mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full overflow-hidden bg-emerald-100 flex items-center justify-center border border-slate-200 shrink-0">
              <img alt="Zha Search Logo" className="w-full h-full object-cover" src="/zha-logo.png"/>
            </div>
            <div>
              <h1 className="font-display text-sm font-bold text-slate-900">Super Admin</h1>
              <p className="text-[10px] text-slate-500">Zha Search Panel</p>
            </div>
          </div>
        </div>

        <div className="flex-grow">
          <ul className="flex flex-col gap-1 font-label-sm text-xs font-semibold">
            {TABS.map((t: TabItem) => {
              const active = tab === t.key;
              const materialIcon = getMaterialIcon(t.key);
              return (
                <li key={t.key}>
                  <button
                    onClick={() => setTab(t.key)}
                    className={`w-full flex items-center gap-3 rounded-lg px-4 py-2.5 transition-all cursor-pointer ${
                      active
                        ? 'bg-emerald-600 text-white font-bold shadow-sm shadow-emerald-600/20'
                        : 'text-slate-700 hover:bg-slate-100 hover:text-slate-900'
                    }`}
                  >
                    <span className={`material-symbols-outlined text-[18px] ${active ? 'icon-fill' : ''}`}>{materialIcon}</span>
                    <span className="flex-grow text-left">{t.label}</span>
                    {t.key === 'listings' && pendingCount > 0 && (
                      <span className="bg-red-500 text-white text-[9px] font-bold px-2 py-0.5 rounded-full shrink-0">
                        {pendingCount}
                      </span>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>

        <div className="mt-auto pt-4 border-t border-outline-variant/20 space-y-4">
          <button
            onClick={() => {
              const headers = 'id,name,city,category,status,view_count\n';
              const rows = 'mock-1,Sharma Electronics,Delhi,Retail,published,14205\nmock-2,Balaji Plumbers,Chennai,Plumbing,published,342\n';
              const blob = new Blob([headers + rows], { type: 'text/csv' });
              const url = window.URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.setAttribute('href', url);
              a.setAttribute('download', `listings_report_${new Date().toISOString().slice(0,10)}.csv`);
              a.click();
            }}
            className="w-full bg-primary text-on-primary font-bold rounded-lg py-2 px-4 flex items-center justify-center gap-2 hover:bg-primary-container transition-colors shadow-sm active:scale-95 text-xs cursor-pointer"
          >
            <span className="material-symbols-outlined text-sm">download</span>
            Download Reports
          </button>
          
          <ul className="flex flex-col gap-1 text-xs font-semibold">
            <li>
              <button
                onClick={onExit}
                className="w-full flex items-center gap-3 text-on-surface-variant hover:bg-surface-container-high rounded-lg px-4 py-2.5 transition-all cursor-pointer text-left"
              >
                <span className="material-symbols-outlined text-[18px]">logout</span>
                Logout / Exit Panel
              </button>
            </li>
          </ul>
        </div>
      </nav>

      {/* Main Content Area */}
      <main className="flex-1 md:ml-64 flex flex-col min-h-screen bg-white overflow-y-auto">
        {/* TopNavBar (Mobile Only Header) */}
        <header className="md:hidden flex justify-between items-center w-full px-6 h-16 bg-white border-b border-slate-200 sticky top-0 z-50">
          <div className="font-display text-lg font-extrabold text-slate-800">Zha Search</div>
          <div className="flex items-center gap-2">
            <button
              onClick={onExit}
              className="text-slate-600 p-2 rounded-full hover:bg-slate-100 cursor-pointer"
            >
              <span className="material-symbols-outlined">logout</span>
            </button>
          </div>
        </header>

        {/* Inner Page View Wrapper */}
        <div className="p-6 md:p-8 max-w-container-max mx-auto w-full flex-grow bg-white text-slate-800">
          {tab === 'overview' && <Overview onNavigate={setTab as (t: string) => void} />}
          {tab === 'automation' && <AutomationHub />}
          {tab === 'image_enrichment' && <ImageEnrichmentAdmin />}
          {tab === 'phone_enrichment' && <PhoneEnrichmentAdmin />}
          {tab === 'connectors' && <Connectors />}
          {tab === 'pipeline' && <Pipeline />}
          {tab === 'leads' && <LeadsPage />}
          {tab === 'listings' && <Listings />}
          {tab === 'business_owners' && <BusinessOwners />}
          {tab === 'blogs' && <BlogManagement />}
          {tab === 'menu' && <MenuManagement />}
          {tab === 'categories' && <CategoriesManager />}
          {tab === 'cities' && <CitiesManager />}
          {tab === 'claims' && <ClaimsManager />}
          {tab === 'offers_events' && <OffersEventsTracker />}
          {tab === 'users' && <VisitorsLog />}
          {tab === 'duplicates' && <Duplicates />}
          {tab === 'bulk' && <BulkImport />}
          {tab === 'google_import' && <GooglePlacesImport />}
          {tab === 'analytics' && <Analytics />}
          {tab === 'settings' && <SettingsPage />}
        </div>
      </main>

      {/* Floating Notification Panel Trigger in Content Bar */}
      <div className="fixed bottom-4 right-4 z-50">
        <button
          onClick={() => setOpenNotifs(v => !v)}
          className="relative w-12 h-12 bg-primary text-on-primary rounded-full shadow-lg flex items-center justify-center hover:bg-primary-container active:scale-95 transition-all cursor-pointer"
        >
          <span className="material-symbols-outlined">notifications</span>
          {unread > 0 && (
            <span className="absolute top-0 right-0 w-3 h-3 bg-error rounded-full border border-white"></span>
          )}
        </button>
        {openNotifs && (
          <div className="absolute right-0 bottom-14 w-80 rounded-xl bg-white border border-outline-variant/30 shadow-2xl overflow-hidden text-left z-50">
            <div className="p-3 border-b border-outline-variant/20 text-xs font-semibold text-on-surface flex items-center justify-between">
              <span>Notifications</span>
              {unread > 0 && (
                <button
                  onClick={markAllAsRead}
                  className="text-xs text-primary hover:underline cursor-pointer font-bold"
                >
                  Mark all as read
                </button>
              )}
            </div>
            <div className="max-h-96 overflow-y-auto no-scrollbar">
              {notifs.length === 0 && (
                <div className="p-4 text-xs text-on-surface-variant">No notifications yet.</div>
              )}
              {notifs.map((n) => (
                <div
                  key={n.id}
                  onClick={() => !n.read && markAsRead(n.id)}
                  className={`p-3 border-b border-outline-variant/10 hover:bg-surface-container-low cursor-pointer flex items-start gap-2 ${
                    !n.read ? 'bg-primary/5' : 'opacity-70'
                  }`}
                >
                  <div className="flex-grow min-w-0">
                    <div className="text-xs font-bold text-on-surface flex items-center justify-between gap-2">
                      <span>{n.title}</span>
                      {!n.read && <span className="h-1.5 w-1.5 rounded-full bg-primary shrink-0" />}
                    </div>
                    {n.body && <div className="text-[10px] text-on-surface-variant mt-0.5">{n.body}</div>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
